import { environment } from 'src/environments/environment';
import * as helpers from '../../helpers/ui-helpers';
import { DateTime, Duration } from 'luxon';
import { NonServicePeriod, NonServicePeriodTypeEnum } from 'src/app/types/non-service-period.type';
import { BuildServices, GroupActionSortByType, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from './generic-route-event.model';
import { _NonServicePeriod } from '.';
import { BaseJob } from '../job.type';
import { Capacitor } from '@capacitor/core';
import { interval, map, takeUntil, takeWhile, tap } from 'rxjs';

export class Break extends _NonServicePeriod {

  public hasTimeConstraints: boolean = environment.features.enforceScheduledBreakTimes;
  public earliestStartTime: DateTime | null = null;
  public earliestEndTime: DateTime | null = null;
  public startedTime: DateTime | null = null;
  public title = this.translateSvc.translate('LABEL.break');
  public type = 'break';
  private _timer$ = interval(2000).pipe(
    map(() => this.getTimerValue()),
    takeWhile(value => value !== '00:00', true),
    tap(value => this.timer = value),
    takeUntil(this.destroyed$),
  );
  public finishTime: DateTime;
  public duration: Duration;

  constructor(data: NonServicePeriod | BaseJob, services: BuildServices) {
    super(data, services);
    this.typeIcons = [ helpers.TypeIcons.break ];
    const { breakStartEarlinessLimit = 0, breakEndEarlinessLimit = 0 } = this.appData.route;

    this.title = helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: services.translateSvc });
    this.earliestStartTime = this.scheduledStart.minus({ seconds: breakStartEarlinessLimit });
    this.earliestEndTime = this.scheduledEnd.minus({ seconds: breakEndEarlinessLimit });
    this.headerInfo.type = 'LABEL.break';
    this.id = this.baseJob.stopId || 'break';
    this.groupStopOrder = this.started ? GroupActionSortByType.BreakEnd : GroupActionSortByType.BreakStart;
    this.duration = this.scheduledEnd.diff(this.scheduledStart);
    this.timer = this.started ? this.getTimerValue() : '';
    this.startedTime = this.baseJob.arriveTime ? DateTime.fromISO(this.baseJob.arriveTime.toString()) : null;
    if (this.started) {
      this.finishTime = DateTime.fromISO(this.baseJob.arriveTime).plus({ minutes: this.duration.as('minutes')});
      this._timer$.subscribe();
    } else {
      this.finishTime = this.scheduledStart.plus({ minutes: this.duration.as('minutes')});
    }
    this.nonServicePeriods.nonServicePeriodType = NonServicePeriodTypeEnum.Break;
    this.actions = this.getActions();
  }

  public getActions(): RouteEventAction[] {
    if (this.completed) { return []; }
    const actions = [
      {
        show: !this.started && (!this.hasTimeConstraints || helpers.isInThePast(this.earliestStartTime)),
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('ACTION.start'),
        icon: 'alarm',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: async (action: RouteEventAction) => {
          await this.start(action);
          this.groupStopOrder = GroupActionSortByType.BreakStart;
        },
      },
      {
        show: this.started && (!this.hasTimeConstraints || helpers.isInThePast(this.earliestEndTime)),
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('ACTION.finish'),
        icon: 'checkmark-circle-outline',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.end(action),
      },
      {
        show: this.location && helpers.hasValidGeo(this.location),
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ];

    return actions.filter(action => action.show);
  }

  protected async start(action: RouteEventAction) {
    try {
      if (action) action.disabled = true;
      if (this.baseJob.stopType) {
        await this.dispatchSvc.arriveJobs([ this ]);
        const now = DateTime.now();
        this.startedTime = now;
        this.finishTime = now.plus({ minutes: this.duration.as('minutes') });
        this.baseJob.arriveTime = now.toString();
        this.appData.setJobs([ this.baseJob ]);
      } else {
        await this.routeProviderSvc.startNonServicePeriod(this.nonServicePeriods.nonServicePeriodType);
        this.nonServicePeriods.hasBeenStarted = true;
      }
      this.started = true;
      this._timer$.subscribe();
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  }

  protected async end(action: RouteEventAction) {
    try {
      if (action) action.disabled = true;
      if (this.baseJob.stopType) {
        await this.dispatchSvc.departJobs([ this ]);
        this.baseJob.departedTime = DateTime.now().toString();
        this.baseJob.completed = true;
        this.appData.setJobs([ this.baseJob ]);
      } else {
        await this.routeProviderSvc.finishNonServicePeriod(this.nonServicePeriods.nonServicePeriodType);
        this.nonServicePeriods.hasBeenCompleted = true;
      }
      this.completed = true;
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  }

  private getTimerValue(): string {
    const duration = this.duration.as('minutes');
    if (!this.startedTime) { return this.formatDiff(duration); }
    const timeElapsedSinceStart = (DateTime.now()).diff(this.startedTime || this.scheduledStart).as('minutes');
    const timeLeft = duration - timeElapsedSinceStart;
    return timeLeft < 0 ? '00:00' : this.formatDiff(timeLeft);
  }

  private formatDiff(diff: number) {
    let hours = Math.floor(diff / 60).toString();
    if (hours.length === 1) hours = '0' + hours;
    let minutes = Math.round((diff % 60)).toString();
    if (minutes.length === 1) minutes = '0' + minutes;
    return `${hours}:${minutes}`;
  }
}
